import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Eye, CheckCircle, XCircle, RefreshCw, Search, Home, MapPin, Bed, Bath, DollarSign, Calendar, Pencil } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";

interface Property {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  unit: string | null;
  bedrooms: number;
  bathrooms: number;
  rent_amount: number;
  status: string;
  property_type: string | null;
  description: string | null;
  square_footage: number | null;
  submitted_by: string | null;
  submitted_at: string | null;
  created_at: string;
  landlord_id: string;
  security_deposit: number | null;
  minimum_lease_months: number | null;
  date_available: string | null;
  property_website: string | null;
  pets_dogs_allowed: boolean | null;
  pets_cats_allowed: boolean | null;
  pets_other_allowed: boolean | null;
  pets_max_dogs: number | null;
  pets_max_cats: number | null;
  pets_deposit: number | null;
  pets_fee: number | null;
}

interface PropertyImage {
  id: string;
  file_path: string;
  file_name: string;
  is_primary: boolean;
  display_order: number;
}

interface PropertyStats {
  total: number;
  pending: number;
  approved: number;
  available: number;
}

interface EditFormData {
  name: string;
  address: string;
  unit: string;
  city: string;
  state: string;
  zip: string;
  bedrooms: string;
  bathrooms: string;
  rent_amount: string;
  property_type: string;
  description: string;
  square_footage: string;
  status: string;
}

export default function AdminProperties() {
  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState<Property[]>([]);
  const [stats, setStats] = useState<PropertyStats>({ total: 0, pending: 0, approved: 0, available: 0 });
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [page, setPage] = useState(0);
  const perPage = 20;
  const [currentUserName, setCurrentUserName] = useState<string>("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkApproving, setBulkApproving] = useState(false);
  const [bulkRejecting, setBulkRejecting] = useState(false);

  const [viewOpen, setViewOpen] = useState(false);
  const [viewProperty, setViewProperty] = useState<Property | null>(null);
  const [viewImages, setViewImages] = useState<{ url: string; is_primary: boolean; file_name: string }[]>([]);
  const [viewLandlord, setViewLandlord] = useState<{ email: string | null; contact_phone: string | null; company: string | null } | null>(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editProperty, setEditProperty] = useState<Property | null>(null);
  const [editForm, setEditForm] = useState<EditFormData>({
    name: "",
    address: "",
    unit: "",
    city: "",
    state: "",
    zip: "",
    bedrooms: "",
    bathrooms: "",
    rent_amount: "",
    property_type: "",
    description: "",
    square_footage: "",
    status: "",
  });
  const [saving, setSaving] = useState(false);

  const fetchProperties = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("properties")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;

      setProperties(data || []);

      // Calculate stats
      const allProperties = data || [];
      setStats({
        total: allProperties.length,
        pending: allProperties.filter(p => p.status === "pending").length,
        approved: allProperties.filter(p => p.status === "approved").length,
        available: allProperties.filter(p => p.status === "available").length,
      });
    } catch (e: any) {
      console.error(e);
      toast.error("Failed to load properties");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProperties();
    fetchCurrentUserName();
  }, []);

  const fetchCurrentUserName = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data: profileData } = await supabase
      .from("profiles")
      .select("first_name, last_name")
      .eq("id", user.id)
      .maybeSingle();

    if (profileData) {
      const fullName = [profileData.first_name, profileData.last_name].filter(Boolean).join(" ");
      setCurrentUserName(fullName || user.email || "Unknown");
    } else {
      setCurrentUserName(user.email || "Unknown");
    }
  };

  const filtered = useMemo(() => {
    let result = properties;

    // Filter by status
    if (statusFilter !== "all") {
      result = result.filter(p => p.status === statusFilter);
    }

    // Filter by search
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(p =>
        p.name?.toLowerCase().includes(q) ||
        p.address?.toLowerCase().includes(q) ||
        p.city?.toLowerCase().includes(q) ||
        p.state?.toLowerCase().includes(q) ||
        p.submitted_by?.toLowerCase().includes(q)
      );
    }

    return result;
  }, [properties, statusFilter, search]);

  const paginatedProperties = useMemo(() => {
    const start = page * perPage;
    return filtered.slice(start, start + perPage);
  }, [filtered, page]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / perPage));

  const handleView = async (property: Property) => {
    setViewProperty(property);
    setViewImages([]);
    setViewLandlord(null);
    setViewOpen(true);

    supabase
      .from("profiles")
      .select("email, contact_phone, company")
      .eq("id", property.landlord_id)
      .maybeSingle()
      .then(({ data }) => {
        setViewLandlord({
          email: data?.email ?? null,
          contact_phone: data?.contact_phone ?? null,
          company: data?.company ?? null,
        });
      });

    const { data: imgs } = await supabase
      .from("property_images")
      .select("id, file_path, file_name, is_primary, display_order")
      .eq("property_id", property.id)
      .order("is_primary", { ascending: false })
      .order("display_order", { ascending: true });
    if (imgs) {
      const mapped = imgs.map((img) => {
        const { data } = supabase.storage.from("property-images").getPublicUrl(img.file_path);
        return { url: data.publicUrl, is_primary: img.is_primary, file_name: img.file_name };
      });
      setViewImages(mapped);
    }
  };

  const handleEdit = (property: Property) => {
    setEditProperty(property);
    setEditForm({
      name: property.name || "",
      address: property.address || "",
      unit: property.unit || "",
      city: property.city || "",
      state: property.state || "",
      zip: property.zip || "",
      bedrooms: property.bedrooms?.toString() || "",
      bathrooms: property.bathrooms?.toString() || "",
      rent_amount: property.rent_amount?.toString() || "",
      property_type: property.property_type || "",
      description: property.description || "",
      square_footage: property.square_footage?.toString() || "",
      status: property.status || "pending",
    });
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editProperty) return;

    setSaving(true);
    try {
      const updateData = {
        name: editForm.name,
        address: editForm.address,
        unit: editForm.unit || null,
        city: editForm.city,
        state: editForm.state,
        zip: editForm.zip,
        bedrooms: parseInt(editForm.bedrooms) || 0,
        bathrooms: parseFloat(editForm.bathrooms) || 0,
        rent_amount: parseFloat(editForm.rent_amount) || 0,
        property_type: editForm.property_type || null,
        description: editForm.description || null,
        square_footage: editForm.square_footage ? parseFloat(editForm.square_footage) : null,
        status: editForm.status,
        submitted_by: currentUserName,
        submitted_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from("properties")
        .update(updateData)
        .eq("id", editProperty.id);

      if (error) throw error;

      toast.success("Property updated successfully");
      setEditOpen(false);
      setEditProperty(null);
      fetchProperties();
    } catch (e: any) {
      console.error(e);
      toast.error("Failed to update property");
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async (property: Property) => {
    try {
      const { error } = await supabase
        .from("properties")
        .update({ status: "approved" })
        .eq("id", property.id);

      if (error) throw error;

      toast.success("Property approved");
      fetchProperties();
    } catch (e: any) {
      console.error(e);
      toast.error("Failed to approve property");
    }
  };

  const handleReject = async (property: Property) => {
    if (!confirm("Are you sure you want to reject this property?")) return;

    try {
      const { error } = await supabase
        .from("properties")
        .update({ status: "rejected" })
        .eq("id", property.id);

      if (error) throw error;

      toast.success("Property rejected");
      fetchProperties();
    } catch (e: any) {
      console.error(e);
      toast.error("Failed to reject property");
    }
  };

  const handleBulkApprove = async () => {
    if (selectedIds.length === 0) return;
    if (!confirm(`Approve ${selectedIds.length} selected property submission(s)?`)) return;
    setBulkApproving(true);
    try {
      const { error } = await supabase
        .from("properties")
        .update({ status: "approved" })
        .in("id", selectedIds);
      if (error) throw error;
      toast.success(`${selectedIds.length} property submission(s) approved`);
      setSelectedIds([]);
      fetchProperties();
    } catch (e: any) {
      console.error(e);
      toast.error("Failed to approve selected properties");
    } finally {
      setBulkApproving(false);
    }
  };

  const handleBulkReject = async () => {
    if (selectedIds.length === 0) return;
    if (!confirm(`Reject ${selectedIds.length} selected property submission(s)?`)) return;
    setBulkRejecting(true);
    try {
      const { error } = await supabase
        .from("properties")
        .update({ status: "rejected" })
        .in("id", selectedIds);
      if (error) throw error;
      toast.success(`${selectedIds.length} property submission(s) rejected`);
      setSelectedIds([]);
      fetchProperties();
    } catch (e: any) {
      console.error(e);
      toast.error("Failed to reject selected properties");
    } finally {
      setBulkRejecting(false);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">Pending</Badge>;
      case "approved":
        return <Badge variant="default" className="bg-green-50 text-green-700 border-green-200">Approved</Badge>;
      case "available":
        return <Badge variant="secondary" className="bg-blue-50 text-blue-700 border-blue-200">Available</Badge>;
      case "rejected":
        return <Badge variant="destructive">Rejected</Badge>;
      case "occupied":
        return <Badge variant="secondary">Occupied</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Properties</CardTitle>
            <Home className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Approval</CardTitle>
            <Calendar className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Approved</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats.approved}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Available</CardTitle>
            <Home className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{stats.available}</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Properties Management</CardTitle>
          <CardDescription>
            Review and manage all property listings in the system.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row md:items-center gap-3 justify-between mb-4">
            <div className="flex items-center gap-2 w-full md:max-w-md">
              <Search className="h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search properties..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="available">Available</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="occupied">Occupied</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={fetchProperties} disabled={loading}>
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </Button>
            </div>
          </div>

          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <Badge variant="secondary">Showing: {filtered.length}</Badge>
            <Badge variant="outline">Page {page + 1} / {pageCount}</Badge>
            {selectedIds.length > 0 && (
              <>
                <Badge>{selectedIds.length} selected</Badge>
                <Button size="sm" onClick={handleBulkApprove} disabled={bulkApproving || bulkRejecting}>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  {bulkApproving ? "Approving..." : `Approve ${selectedIds.length} selected`}
                </Button>
                <Button size="sm" variant="destructive" onClick={handleBulkReject} disabled={bulkApproving || bulkRejecting}>
                  <XCircle className="h-4 w-4 mr-2" />
                  {bulkRejecting ? "Rejecting..." : `Reject ${selectedIds.length} selected`}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setSelectedIds([])} disabled={bulkApproving || bulkRejecting}>
                  Clear selection
                </Button>
              </>
            )}
          </div>

          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead style={{ width: 40 }}>
                    {(() => {
                      const pendingOnPage = paginatedProperties.filter((p) => p.status === "pending");
                      const allChecked =
                        pendingOnPage.length > 0 &&
                        pendingOnPage.every((p) => selectedIds.includes(p.id));
                      return (
                        <Checkbox
                          checked={allChecked}
                          disabled={pendingOnPage.length === 0}
                          onCheckedChange={(checked) => {
                            const ids = pendingOnPage.map((p) => p.id);
                            if (checked) {
                              setSelectedIds((prev) => Array.from(new Set([...prev, ...ids])));
                            } else {
                              setSelectedIds((prev) => prev.filter((id) => !ids.includes(id)));
                            }
                          }}
                          aria-label="Select all pending on page"
                        />
                      );
                    })()}
                  </TableHead>
                  <TableHead>Property</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead style={{ width: 100 }}>Beds/Baths</TableHead>
                  <TableHead style={{ width: 100 }}>Rent</TableHead>
                  <TableHead style={{ width: 120 }}>Status</TableHead>
                  <TableHead style={{ width: 150 }}>Submitted By</TableHead>
                  <TableHead style={{ width: 120 }}>Submitted At</TableHead>
                  <TableHead style={{ width: 200 }}>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedProperties.map((property) => (
                  <TableRow key={property.id} data-state={selectedIds.includes(property.id) ? "selected" : undefined}>
                    <TableCell>
                      {property.status === "pending" ? (
                        <Checkbox
                          checked={selectedIds.includes(property.id)}
                          onCheckedChange={() => toggleSelect(property.id)}
                          aria-label="Select property"
                        />
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{property.name}</div>
                      <div className="text-sm text-muted-foreground">{property.property_type || "—"}</div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <MapPin className="h-3 w-3 text-muted-foreground" />
                        <span className="text-sm">
                          {property.address}, {property.city}, {property.state}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 text-sm">
                        <span className="flex items-center gap-1">
                          <Bed className="h-3 w-3" /> {property.bedrooms}
                        </span>
                        <span className="flex items-center gap-1">
                          <Bath className="h-3 w-3" /> {property.bathrooms}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <DollarSign className="h-3 w-3" />
                        {property.rent_amount?.toLocaleString()}
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(property.status)}</TableCell>
                    <TableCell className="text-sm">{property.submitted_by || "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {property.submitted_at
                        ? format(new Date(property.submitted_at), "MMM d, yyyy")
                        : property.created_at
                        ? format(new Date(property.created_at), "MMM d, yyyy")
                        : "—"}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="outline" onClick={() => handleView(property)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleEdit(property)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        {property.status === "pending" && (
                          <>
                            <Button size="sm" variant="default" onClick={() => handleApprove(property)}>
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => handleReject(property)}>
                              <XCircle className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}

                {!loading && paginatedProperties.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center text-muted-foreground py-8">
                      No properties found.
                    </TableCell>
                  </TableRow>
                )}

                {loading && (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center text-muted-foreground py-8">
                      Loading…
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between mt-4">
            <Button
              variant="outline"
              disabled={page <= 0 || loading}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Previous
            </Button>
            <div className="text-sm text-muted-foreground">
              Showing {paginatedProperties.length} of {filtered.length} properties
            </div>
            <Button
              variant="outline"
              disabled={page >= pageCount - 1 || loading}
              onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
            >
              Next
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* View Property Dialog */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Property Details</DialogTitle>
            <DialogDescription>
              Full details for {viewProperty?.name}
            </DialogDescription>
          </DialogHeader>
          {viewProperty && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Property Name</p>
                  <p className="text-lg font-semibold">{viewProperty.name}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Status</p>
                  <div className="mt-1">{getStatusBadge(viewProperty.status)}</div>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-muted-foreground">Address</p>
                <p>{viewProperty.address}{viewProperty.unit ? `, Unit ${viewProperty.unit}` : ""}</p>
                <p>{viewProperty.city}, {viewProperty.state} {viewProperty.zip}</p>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Bedrooms</p>
                  <p className="text-lg">{viewProperty.bedrooms}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Bathrooms</p>
                  <p className="text-lg">{viewProperty.bathrooms}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Rent</p>
                  <p className="text-lg">${viewProperty.rent_amount?.toLocaleString()}/mo</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Property Type</p>
                  <p>{viewProperty.property_type || "Not specified"}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Square Footage</p>
                  <p>{viewProperty.square_footage ? `${viewProperty.square_footage} sq ft` : "Not specified"}</p>
                </div>
              </div>

              {viewProperty.description && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Description</p>
                  <p className="text-sm">{viewProperty.description}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 pt-2 border-t">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Security Deposit</p>
                  <p>{viewProperty.security_deposit != null ? `$${Number(viewProperty.security_deposit).toLocaleString()}` : "Not specified"}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Minimum Lease</p>
                  <p>{viewProperty.minimum_lease_months ? `${viewProperty.minimum_lease_months} months` : "Not specified"}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Date Available</p>
                  <p>{viewProperty.date_available ? format(new Date(viewProperty.date_available), "PPP") : "Not specified"}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Property Website</p>
                  {viewProperty.property_website ? (
                    <a
                      href={viewProperty.property_website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary underline break-all"
                    >
                      {viewProperty.property_website}
                    </a>
                  ) : (
                    <p>Not specified</p>
                  )}
                </div>
              </div>

              <div className="pt-2 border-t">
                <p className="text-sm font-medium text-muted-foreground mb-2">Pets</p>
                {(viewProperty.pets_dogs_allowed || viewProperty.pets_cats_allowed || viewProperty.pets_other_allowed) ? (
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Dogs</p>
                      <p>{viewProperty.pets_dogs_allowed ? `Allowed${viewProperty.pets_max_dogs ? ` (max ${viewProperty.pets_max_dogs})` : ""}` : "Not allowed"}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Cats</p>
                      <p>{viewProperty.pets_cats_allowed ? `Allowed${viewProperty.pets_max_cats ? ` (max ${viewProperty.pets_max_cats})` : ""}` : "Not allowed"}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Other</p>
                      <p>{viewProperty.pets_other_allowed ? "Allowed" : "Not allowed"}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm">No pets allowed</p>
                )}
                <div className="grid grid-cols-2 gap-4 mt-3">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Pet Deposit</p>
                    <p>{viewProperty.pets_deposit != null ? `$${Number(viewProperty.pets_deposit).toLocaleString()}` : "Not specified"}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Pet Fee</p>
                    <p>{viewProperty.pets_fee != null ? `$${Number(viewProperty.pets_fee).toLocaleString()}` : "Not specified"}</p>
                  </div>
                </div>
              </div>

              <div className="pt-2 border-t">
                <p className="text-sm font-medium text-muted-foreground mb-2">
                  Photos {viewImages.length > 0 && `(${viewImages.length})`}
                </p>
                {viewImages.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No photos uploaded</p>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {viewImages.map((img, idx) => (
                      <a
                        key={idx}
                        href={img.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="relative block aspect-video overflow-hidden rounded border bg-muted"
                      >
                        <img
                          src={img.url}
                          alt={img.file_name}
                          loading="lazy"
                          className="h-full w-full object-cover"
                        />
                        {img.is_primary && (
                          <span className="absolute top-1 left-1 bg-primary text-primary-foreground text-[10px] px-1.5 py-0.5 rounded">
                            Primary
                          </span>
                        )}
                      </a>
                    ))}
                  </div>
                )}
              </div>


              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Submitted By</p>
                  <p>{viewProperty.submitted_by || "Not specified"}</p>
                  <div className="mt-1 space-y-0.5 text-sm text-muted-foreground">
                    {viewLandlord?.email && (
                      <p>
                        <a href={`mailto:${viewLandlord.email}`} className="hover:underline">
                          {viewLandlord.email}
                        </a>
                      </p>
                    )}
                    {viewLandlord?.contact_phone && (
                      <p>
                        <a href={`tel:${viewLandlord.contact_phone}`} className="hover:underline">
                          {viewLandlord.contact_phone}
                        </a>
                      </p>
                    )}
                    {viewLandlord?.company && <p>{viewLandlord.company}</p>}
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Submitted At</p>
                  <p>
                    {viewProperty.submitted_at
                      ? format(new Date(viewProperty.submitted_at), "PPP p")
                      : "—"}
                  </p>
                </div>
              </div>


              <div className="flex gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => { setViewOpen(false); handleEdit(viewProperty); }}>
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit Property
                </Button>
                {viewProperty.status === "pending" && (
                  <>
                    <Button onClick={() => { handleApprove(viewProperty); setViewOpen(false); }}>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Approve
                    </Button>
                    <Button variant="destructive" onClick={() => { handleReject(viewProperty); setViewOpen(false); }}>
                      <XCircle className="h-4 w-4 mr-2" />
                      Reject
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Property Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Property</DialogTitle>
            <DialogDescription>
              Update property details for {editProperty?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Property Name</Label>
                <Input
                  id="name"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  placeholder="Property name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="property_type">Property Type</Label>
                <Select
                  value={editForm.property_type}
                  onValueChange={(value) => setEditForm({ ...editForm, property_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="house">House</SelectItem>
                    <SelectItem value="apartment">Apartment</SelectItem>
                    <SelectItem value="condo">Condo</SelectItem>
                    <SelectItem value="townhouse">Townhouse</SelectItem>
                    <SelectItem value="duplex">Duplex</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-4 gap-4">
              <div className="col-span-2 space-y-2">
                <Label htmlFor="address">Street Address</Label>
                <Input
                  id="address"
                  value={editForm.address}
                  onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                  placeholder="123 Main St"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="unit">Unit</Label>
                <Input
                  id="unit"
                  value={editForm.unit}
                  onChange={(e) => setEditForm({ ...editForm, unit: e.target.value })}
                  placeholder="Apt 1"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="zip">ZIP Code</Label>
                <Input
                  id="zip"
                  value={editForm.zip}
                  onChange={(e) => setEditForm({ ...editForm, zip: e.target.value })}
                  placeholder="12345"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={editForm.city}
                  onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                  placeholder="City"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="state">State</Label>
                <Input
                  id="state"
                  value={editForm.state}
                  onChange={(e) => setEditForm({ ...editForm, state: e.target.value })}
                  placeholder="TX"
                />
              </div>
            </div>

            <div className="grid grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label htmlFor="bedrooms">Bedrooms</Label>
                <Input
                  id="bedrooms"
                  type="number"
                  value={editForm.bedrooms}
                  onChange={(e) => setEditForm({ ...editForm, bedrooms: e.target.value })}
                  placeholder="3"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bathrooms">Bathrooms</Label>
                <Input
                  id="bathrooms"
                  type="number"
                  step="0.5"
                  value={editForm.bathrooms}
                  onChange={(e) => setEditForm({ ...editForm, bathrooms: e.target.value })}
                  placeholder="2"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="rent_amount">Rent ($)</Label>
                <Input
                  id="rent_amount"
                  type="text"
                  inputMode="decimal"
                  value={editForm.rent_amount}
                  onChange={(e) => setEditForm({ ...editForm, rent_amount: e.target.value })}
                  placeholder="1500"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="square_footage">Sq Ft</Label>
                <Input
                  id="square_footage"
                  type="number"
                  value={editForm.square_footage}
                  onChange={(e) => setEditForm({ ...editForm, square_footage: e.target.value })}
                  placeholder="1200"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={editForm.status}
                onValueChange={(value) => setEditForm({ ...editForm, status: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="available">Available</SelectItem>
                  <SelectItem value="occupied">Occupied</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                placeholder="Property description..."
                rows={4}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={saving}>
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
